/**
 * Client bootstrap for Vite entry: dev-only why-did-you-render, route-change side effects
 * (previousPath + analytics), and non-production API mock setup.
 * Client bootstrap (replaces legacy gatsby-browser.js): onClientEntry, onRouteUpdate, api mocks.
 */
import React from 'react';

import { getFirebaseAnalytics } from '~/apis/firebase';
import { DEBUG_logWhyDidYouRender, isAnalyticsEnabled } from '~/app/config';
import { env } from '~/app/env';

import { apiLogger } from './logger';

declare global {
  interface Window {
    locations?: string[];
    previousPath?: string;
    excludeGtagPaths?: RegExp[];
  }
}

/** Call on initial load and whenever the router navigates (e.g. TanStack Router in Task 4). */
export function reportRouteUpdate(location: {
  pathname: string;
  search?: string;
  hash?: string;
}): void {
  if (typeof window === 'undefined') return;

  window.locations = window.locations ?? [document.referrer];
  const href = location.pathname + (location.search ?? '') + (location.hash ?? '');
  if (window.locations[window.locations.length - 1] !== href) {
    window.locations.push(href);
  }
  window.previousPath = window.locations[window.locations.length - 2];

  if (!isAnalyticsEnabled) return;
  const analytics = getFirebaseAnalytics();
  if (!analytics) return;
  const pathIsExcluded =
    typeof window.excludeGtagPaths !== 'undefined' &&
    window.excludeGtagPaths.some((rx) => rx.test(location.pathname));
  if (pathIsExcluded) return;

  const sendPageView = () => {
    const pagePath = location.pathname + (location.search ?? '') + (location.hash ?? '');
    analytics.logEvent('page_view', { page_path: pagePath });
  };
  if ('requestAnimationFrame' in window) {
    requestAnimationFrame(() => requestAnimationFrame(sendPageView));
  } else {
    setTimeout(sendPageView, 32);
  }
}

export function bootstrap(): void {
  if (env.dev && DEBUG_logWhyDidYouRender) {
    void import('@welldone-software/why-did-you-render')
      .then(({ default: whyDidYouRender }) => {
        whyDidYouRender(React, {
          trackAllPureComponents: DEBUG_logWhyDidYouRender,
        });
      })
      .catch(() => {});
  }

  reportRouteUpdate({
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  });

  if (!env.prod) {
    import('~/apis/apiMock').then(({ default: setupRuntimeApiMock }) => {
      setupRuntimeApiMock();
      apiLogger.info('!!! mock api enabled !!!');
    });
  }
}
